import { FormEvent, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { partnerApi, type PartnerMember } from '@/services/api/partner'
import type { CountryInfo } from '@/services/api/ultraadmin'
import { extractErrorMessage } from '@/utils/errorHandler'
import { useAuthStore } from '@/store/authStore'

export function PartnerTeamPage() {
  const partnerLevel = useAuthStore((s) => s.user?.partner_level)
  const [members, setMembers] = useState<PartnerMember[]>([])
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [level, setLevel] = useState<'owner' | 'staff'>('staff')
  const [companyName, setCompanyName] = useState('')
  const [companyPhone, setCompanyPhone] = useState('')
  const [companyEmail, setCompanyEmail] = useState('')
  const [companyAddress, setCompanyAddress] = useState('')
  const [companyCity, setCompanyCity] = useState('')
  const [companyZip, setCompanyZip] = useState('')
  const [companyCountry, setCompanyCountry] = useState('')
  const [countries, setCountries] = useState<CountryInfo[]>([])
  const [audit, setAudit] = useState<{ action: string; entity_repr?: string; created_at?: string }[]>([])
  const [editTarget, setEditTarget] = useState<PartnerMember | null>(null)
  const [editFirst, setEditFirst] = useState('')
  const [editLast, setEditLast] = useState('')
  const [editEmail, setEditEmail] = useState('')

  const load = async () => {
    setMembers(await partnerApi.listTeam())
    const c = await partnerApi.getCompany()
    setCompanyName(c.name)
    setCompanyPhone(c.phone || '')
    setCompanyEmail(c.email || '')
    setCompanyAddress(c.address || '')
    setCompanyCity(c.city || '')
    setCompanyZip(c.zip_code || '')
    setCompanyCountry(c.country || '')
    const entries = await partnerApi.listAudit() as { action: string; entity_repr?: string; created_at?: string }[]
    setAudit(Array.isArray(entries) ? entries : [])
  }
  useEffect(() => {
    if (partnerLevel && partnerLevel !== 'owner') return
    load().catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' })))
  }, [partnerLevel])
  useEffect(() => {
    if (partnerLevel && partnerLevel !== 'owner') return
    partnerApi.getCountries().then(setCountries).catch(() => undefined)
  }, [partnerLevel])

  if (partnerLevel && partnerLevel !== 'owner') {
    return <Navigate to="/partner/schools" replace />
  }

  const onInvite = async (e: FormEvent) => {
    e.preventDefault()
    try {
      await partnerApi.inviteMember({ email, first_name: firstName, last_name: lastName, level })
      setEmail(''); setFirstName(''); setLastName('')
      await load()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Einladen fehlgeschlagen' }))
    }
  }

  const openEdit = (m: PartnerMember) => {
    setEditTarget(m)
    setEditFirst(m.first_name || '')
    setEditLast(m.last_name || '')
    setEditEmail(m.email)
  }

  const onSaveMember = async (e: FormEvent) => {
    e.preventDefault()
    if (!editTarget) return
    try {
      await partnerApi.updateMember(editTarget.user_id, {
        first_name: editFirst.trim(),
        last_name: editLast.trim(),
        email: editEmail.trim(),
      })
      setEditTarget(null)
      setError(null)
      await load()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Speichern fehlgeschlagen' }))
    }
  }

  const onSaveCompany = async (e: FormEvent) => {
    e.preventDefault()
    try {
      await partnerApi.patchCompany({
        name: companyName,
        phone: companyPhone,
        email: companyEmail || null,
        address: companyAddress || null,
        city: companyCity || null,
        zip_code: companyZip || null,
        country: companyCountry || null,
      })
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Speichern fehlgeschlagen' }))
    }
  }

  return (
    <div className="p-6 space-y-8">
      <h1 className="text-2xl font-bold">Team</h1>
      {error && <div className="p-3 bg-red-100 text-red-700 rounded">{error}</div>}
      <form onSubmit={onSaveCompany} className="bg-white p-4 rounded-lg shadow max-w-md space-y-2">
        <h2 className="font-semibold">Firma</h2>
        <input className="border w-full p-2" placeholder="Firmenname" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
        <input className="border w-full p-2" placeholder="Adresse" value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)} />
        <input className="border w-full p-2" placeholder="PLZ" value={companyZip} onChange={(e) => setCompanyZip(e.target.value)} />
        <input className="border w-full p-2" placeholder="Ort" value={companyCity} onChange={(e) => setCompanyCity(e.target.value)} />
        <select className="border w-full p-2" value={companyCountry} onChange={(e) => setCompanyCountry(e.target.value)}>
          <option value="">Land</option>
          {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          {companyCountry && !countries.some((c) => c.code === companyCountry) && (
            <option value={companyCountry}>{companyCountry}</option>
          )}
        </select>
        <input className="border w-full p-2" placeholder="Telefon" value={companyPhone} onChange={(e) => setCompanyPhone(e.target.value)} />
        <input className="border w-full p-2" placeholder="E-Mail" type="email" value={companyEmail} onChange={(e) => setCompanyEmail(e.target.value)} />
        <button type="submit" className="bg-blue-600 text-white px-3 py-1 rounded">Speichern</button>
      </form>
      <table className="min-w-full bg-white rounded-lg shadow">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs uppercase">Name</th>
            <th className="px-4 py-2 text-left text-xs uppercase">E-Mail</th>
            <th className="px-4 py-2 text-left text-xs uppercase">Stufe</th>
            <th className="px-4 py-2 text-left text-xs uppercase">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id} className="border-t">
              <td className="px-4 py-2">{m.last_name}, {m.first_name}</td>
              <td className="px-4 py-2">{m.email}</td>
              <td className="px-4 py-2">{m.level === 'owner' ? 'Inhaber' : 'Mitarbeiter'}{m.is_active ? '' : ' (inaktiv)'}</td>
              <td className="px-4 py-2 space-x-2 text-sm">
                <button type="button" className="text-blue-700" onClick={() => openEdit(m)}>Bearbeiten</button>
                {m.level === 'staff'
                  ? <button type="button" onClick={() => partnerApi.updateMember(m.user_id, { level: 'owner' }).then(load).catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' })))}>Zum Inhaber</button>
                  : <button type="button" onClick={() => partnerApi.updateMember(m.user_id, { level: 'staff' }).then(load).catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' })))}>Zum Mitarbeiter</button>}
                {m.is_active && <button type="button" onClick={() => partnerApi.updateMember(m.user_id, { is_active: false }).then(load).catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' })))}>Deaktivieren</button>}
                {!m.is_active && <button type="button" onClick={() => partnerApi.updateMember(m.user_id, { is_active: true }).then(load).catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' })))}>Reaktivieren</button>}
                <button type="button" onClick={() => partnerApi.resendMemberInvite(m.user_id).catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' })))}>Invite erneut</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
          <form onSubmit={onSaveMember} className="bg-white p-6 rounded-lg w-full max-w-md space-y-3">
            <h2 className="font-bold">Mitglied bearbeiten</h2>
            <input className="border w-full p-2" placeholder="Vorname" value={editFirst} onChange={(e) => setEditFirst(e.target.value)} required />
            <input className="border w-full p-2" placeholder="Nachname" value={editLast} onChange={(e) => setEditLast(e.target.value)} required />
            <input className="border w-full p-2" placeholder="E-Mail" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} required />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditTarget(null)}>Abbrechen</button>
              <button type="submit" className="bg-blue-600 text-white px-3 py-1 rounded">Speichern</button>
            </div>
          </form>
        </div>
      )}
      <form onSubmit={onInvite} className="bg-white p-4 rounded-lg shadow max-w-md space-y-2">
        <h2 className="font-semibold">Einladen</h2>
        <input className="border w-full p-2" placeholder="Vorname" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
        <input className="border w-full p-2" placeholder="Nachname" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
        <input className="border w-full p-2" placeholder="E-Mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <select className="border w-full p-2" value={level} onChange={(e) => setLevel(e.target.value as 'owner' | 'staff')}>
          <option value="staff">Mitarbeiter</option>
          <option value="owner">Inhaber</option>
        </select>
        <button type="submit" className="bg-blue-600 text-white px-3 py-1 rounded">Einladen</button>
        <p className="text-xs text-gray-500">Letzter Inhaber kann nicht entfernt oder gestuft werden.</p>
      </form>
      <div className="bg-white p-4 rounded-lg shadow">
        <h2 className="font-semibold mb-2">Audit</h2>
        <ul className="text-sm space-y-1">
          {audit.map((a, i) => (
            <li key={i}>{a.created_at} — {a.action} {a.entity_repr}</li>
          ))}
        </ul>
      </div>
    </div>
  )
}
